import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from "react";
import { cn } from "@/shared/cn";
import { FieldFrame } from "@/shared/ui/primitives/FieldFrame";
import { INPUT_CLASS } from "@/shared/ui/primitives/field-styles";

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  suffix?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, suffix, id, required, className, ...rest },
  ref,
) {
  const ownId = useId();
  const inputId = id ?? ownId;
  return (
    <FieldFrame id={inputId} label={label} required={required} hint={hint} error={error}>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error != null ? true : undefined}
          aria-describedby={
            error != null ? `${inputId}-error` : hint != null ? `${inputId}-hint` : undefined
          }
          className={cn(INPUT_CLASS, suffix != null ? "pr-[44px]" : null, className)}
          {...rest}
        />
        {suffix != null && (
          <span className="pointer-events-none absolute top-1/2 right-[12px] -translate-y-1/2 text-sm text-(--muted)">
            {suffix}
          </span>
        )}
      </div>
    </FieldFrame>
  );
});
